import React from 'react';
import GraphRenderer from './GraphRenderer';
import { TimelineGraphConfig } from './GraphRenderer';

const TimelineTestPage: React.FC = () => {
  // Example from the user - converted to proper timeline format
  const originalTimelineExample: TimelineGraphConfig = {
    type: 'timeline',
    title: "ציר זמן לחיסכון של גב' כהן",
    xRange: [0, 360],
    xLabel: "זמן (חודשים)",
    unit: "חודשים",
    markers: [
      {x: 0, label: "היום"},
      {x: 180, label: "סוף תקופה 1"},
      {x: 240, label: "סוף תקופה 2"},
      {x: 360, label: "סוף תקופה 3 (סופי)"}
    ],
    periods: [
      {start: 0, end: 180, label: "הפקדות (15 שנה)", color: "#3b82f6"},
      {start: 180, end: 240, label: "משיכות (5 שנים)", color: "#ef4444"},
      {start: 240, end: 360, label: "הפקדות נוספות (10 שנים)", color: "#10b981"}
    ],
    description: "הגרף מציג את ציר הזמן של החיסכון בחודשים. התקופה הראשונה היא 15 שנה (180 חודשים) של הפקדות, לאחר מכן 5 שנים (60 חודשים) של משיכות, ולבסוף 10 שנים (120 חודשים) נוספות של הפקדות. היעד הסופי הוא 360 חודשים."
  };

  // Alternative example using events
  const eventsTimelineExample: TimelineGraphConfig = {
    type: 'timeline',
    title: "תכנית לימודים סמסטר א'",
    xRange: [1, 16],
    xLabel: "שבועות",
    unit: "שבוע",
    events: [
      {time: 1, label: "תחילת סמסטר", type: "start"},
      {time: 4, label: "הגשת מטלה 1", type: "event"},
      {time: 7, label: "בוחן אמצע", type: "milestone"},
      {time: 10, label: "הגשת פרויקט", type: "event"},
      {time: 13, label: "סיום הרצאות", type: "event"},
      {time: 16, label: "מבחן סופי", type: "end"}
    ],
    periods: [
      {start: 1, end: 7, label: "חלק א'", color: "#8b5cf6"},
      {start: 7, end: 13, label: "חלק ב'", color: "#3b82f6"},
      {start: 13, end: 16, label: "תקופת מבחנים", color: "#f59e0b"}
    ],
    description: "לוח זמנים של הסמסטר עם אבני דרך חשובות"
  };

  // Project timeline example
  const projectTimelineExample: TimelineGraphConfig = {
    type: 'timeline',
    title: "Timeline פרויקט פיתוח תוכנה",
    xRange: [0, 12],
    xLabel: "חודשים",
    unit: "חודש",
    events: [
      {time: 0, label: "Kickoff", type: "start"},
      {time: 2, label: "Design Complete", type: "milestone"},
      {time: 6, label: "MVP Ready", type: "milestone"},
      {time: 9, label: "Beta Release", type: "milestone"},
      {time: 12, label: "Launch", type: "end"}
    ],
    periods: [
      {start: 0, end: 2, label: "Planning & Design", color: "#6366f1"},
      {start: 2, end: 6, label: "Development", color: "#3b82f6"},
      {start: 6, end: 9, label: "Testing & QA", color: "#10b981"},
      {start: 9, end: 12, label: "Deployment", color: "#f59e0b"}
    ],
    description: "Project development phases with key milestones"
  };

  // Historical timeline
  const historicalTimelineExample: TimelineGraphConfig = {
    type: 'timeline',
    title: "מלחמת העולם השנייה - אירועים מרכזיים",
    xRange: [1939, 1945],
    xLabel: "שנה",
    unit: "",
    events: [
      {time: 1939, label: "פלישה לפולין", type: "start"},
      {time: 1941, label: "פרל הארבור", type: "milestone"},
      {time: 1942, label: "סטלינגרד", type: "milestone"},
      {time: 1944, label: "D-Day", type: "milestone"},
      {time: 1945, label: "סיום המלחמה", type: "end"}
    ],
    description: "ציר זמן של אירועים מרכזיים במלחמת העולם השנייה"
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Timeline Graph Test Page</h1>
        
        <div className="space-y-8">
          {/* Original Example */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Example 1: Financial Planning Timeline (Your Example)</h2>
            <div className="w-full overflow-x-auto">
              <GraphRenderer config={originalTimelineExample} isRTL={true} />
            </div>
            <div className="mt-4 p-4 bg-gray-100 rounded">
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(originalTimelineExample, null, 2)}
              </pre>
            </div>
          </div>

          {/* Events Example */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Example 2: Academic Semester Timeline</h2>
            <div className="w-full overflow-x-auto">
              <GraphRenderer config={eventsTimelineExample} isRTL={true} />
            </div>
            <div className="mt-4 p-4 bg-gray-100 rounded">
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(eventsTimelineExample, null, 2)}
              </pre>
            </div>
          </div>

          {/* Project Timeline */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Example 3: Software Project Timeline</h2>
            <div className="w-full overflow-x-auto">
              <GraphRenderer config={projectTimelineExample} isRTL={false} />
            </div>
            <div className="mt-4 p-4 bg-gray-100 rounded">
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(projectTimelineExample, null, 2)}
              </pre>
            </div>
          </div>

          {/* Historical Timeline */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Example 4: Historical Events Timeline</h2>
            <div className="w-full overflow-x-auto">
              <GraphRenderer config={historicalTimelineExample} isRTL={true} />
            </div>
            <div className="mt-4 p-4 bg-gray-100 rounded">
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(historicalTimelineExample, null, 2)}
              </pre>
            </div>
          </div>
        </div>

        <div className="mt-8 p-6 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Usage Notes:</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Timeline graphs support both <code>events</code> and <code>periods</code></li>
            <li>Events are individual points in time with labels</li>
            <li>Periods are duration blocks that span from start to end</li>
            <li>The <code>unit</code> field adds units to time labels (e.g., "חודשים", "שנים")</li>
            <li>Event types: "start" (green), "end" (red), "milestone" (purple), "event" (blue)</li>
            <li>Colors can be customized for periods</li>
            <li>Compatible with old <code>markers</code> format for backward compatibility</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TimelineTestPage;